import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function ExecutiveTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="bg-slate-900 text-white px-10 py-8 -mx-10 -mt-10 mb-8" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-bold tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${2.25 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        {s.photo && <img src={s.photo} alt="" className="w-24 h-24 rounded-full object-cover mx-auto mt-3 border-2 border-white" />}
        <div className="mt-3 text-sm text-slate-300 space-y-1">
          {personal.email && <p>&#9993; {personal.email}</p>}
          {personal.phone && <p>&#9742; {personal.phone}</p>}
          {personal.location && <p>&#128205; {personal.location}</p>}
          {personal.linkedin && <p>&#128188; {personal.linkedin}</p>}
          {personal.website && <p>&#127760; {personal.website}</p>}
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 flex items-center" style={{ fontSize: `${1.25 * hScale}rem` }}>
              <span className="w-2 h-8 bg-slate-800 mr-3 rounded-sm"></span>
              Work Experience
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id} className="relative pl-6 border-l-2 border-slate-200">
                  <div className="absolute -left-[5px] top-0 w-2 h-2 bg-slate-800 rounded-full"></div>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${1.125 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700 font-semibold mt-1" style={{ fontSize: `${1 * bScale}rem` }}>{exp.company}{exp.location && ` • ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 flex items-center" style={{ fontSize: `${1.25 * hScale}rem` }}>
              <span className="w-2 h-8 bg-slate-800 mr-3 rounded-sm"></span>
              Education
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-baseline pb-4 border-b border-slate-100">
                  <div>
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                    <p className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  </div>
                  <span className="text-slate-500 font-medium" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 flex items-center" style={{ fontSize: `${1.25 * hScale}rem` }}>
              <span className="w-2 h-8 bg-slate-800 mr-3 rounded-sm"></span>
              Skills
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white" style={{ fontSize: `${0.75 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 flex items-center" style={{ fontSize: `${1.25 * hScale}rem` }}>
              <span className="w-2 h-8 bg-slate-800 mr-3 rounded-sm"></span>
              Projects
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {projects.map((proj) => (
                <div key={proj.id} className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                  <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {proj.technologies.map((t) => <span key={t} className="text-xs bg-white text-slate-600 px-2 py-1 rounded border border-slate-200" style={{ fontSize: `${0.75 * bScale}rem` }}>{t}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 flex items-center" style={{ fontSize: `${1.25 * hScale}rem` }}>
              <span className="w-2 h-8 bg-slate-800 mr-3 rounded-sm"></span>
              Certifications
            </h2>
            <div className="flex flex-wrap gap-3">
              {certifications.map((cert) => (
                <div key={cert.id} className="bg-slate-800 text-white px-4 py-2 rounded-lg">
                  <span className="font-semibold" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-300 ml-2" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
